import { LitElement } from 'lit'
import { customElement, property } from 'lit/decorators.js'

import { injectLegacyLightDomStyles } from '../common/light-dom-styles.js'
import style from './Kbd.css'

/**
 * Kbd is used to display a keyboard key, such as a shortcut or a key the user
 * should press. Group related keys together with [Kbd Group](/components/kbd-group/).
 *
 * @status ready
 * @category content
 * @lightDom true
 *
 * @slot - Default slot for the key label (text or an icon).
 *
 * @cssprop [--n-kbd-color=var(--n-color-icon)] - Controls the text color of the key.
 * @cssprop [--n-kbd-background-color=var(--n-color-button)] - Controls the background color of the key.
 * @cssprop [--n-kbd-padding-block] - Controls the block padding of the key. Overrides the size default.
 * @cssprop [--n-kbd-padding-inline] - Controls the inline padding of the key. Overrides the size default.
 * @cssprop [--n-kbd-font-size] - Controls the font size of the key. Overrides the size default.
 * @cssprop [--n-kbd-border-radius=var(--n-border-radius-s)] - Controls how rounded the corners are.
 * @cssprop [--n-kbd-border-width=1px] - Controls the border width of the key.
 * @cssprop [--n-kbd-border-color] - Controls the border color of the key. Overrides the variant default.
 * @cssprop [--n-kbd-box-shadow] - Controls the box shadow of the key. Overrides the variant default.
 */
@customElement('nord-kbd')
export default class Kbd extends LitElement {
  /**
   * The size of the key. Each value maps to the matching font-size token
   * (`xs`→`--n-font-size-xs` … `l`→`--n-font-size-l`); `xs` suits dense
   * surfaces like dark tooltips, `l` is for more prominent keys.
   */
  @property({ reflect: true }) size: 'xs' | 's' | 'm' | 'l' = 'm'

  /**
   * The colour variant. `default` follows the active layout theme; `light` and
   * `dark` pin the key to fixed light/dark colours regardless of the theme
   * (e.g. a dark key on a dark tooltip).
   */
  @property({ reflect: true }) variant: 'default' | 'light' | 'dark' = 'default'

  /**
   * Removes the border and drop shadow, for a flat key with no outline.
   */
  @property({ reflect: true, type: Boolean }) borderless = false

  /**
   * Raises the text contrast — the theme's main text colour (`--n-color-text`)
   * on the `default` variant, or the pinned high-contrast colour (dark text /
   * white) on the fixed `light` / `dark` variants.
   */
  @property({ reflect: true, type: Boolean }) emphasize = false

  connectedCallback() {
    super.connectedCallback()
    injectLegacyLightDomStyles(this, style)
  }

  // Render in the light DOM so the key label is the element's own content and
  // is styled by the injected stylesheet (no shadow boundary, like Table).
  protected createRenderRoot() {
    return this
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'nord-kbd': Kbd
  }
}
